/**
 * PilotWizard — assistant in Super Admin to create a pilot school in one click.
 *
 * Flow:
 *   1. Fill form (name, country, prefix, # teachers, # students per grade, passwords).
 *   2. Submit → POST /api/admin/seed-school-pilot
 *   3. Show printable credential sheet (Imprimir / Descargar PDF via window.print).
 *
 * The endpoint is idempotent: re-running with the same name/country reuses the school.
 */
import React, { useMemo, useState, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles, Printer, RotateCcw, GraduationCap, Users, Shield, Copy } from "lucide-react";

const GRADES = ["4", "5", "6", "7", "8", "9", "10", "11"];

const slugifyPrefix = (str = "") =>
  str.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8) || "COLEGIO";

export default function PilotWizard() {
  // Form state
  const [name, setName] = useState("");
  const [country, setCountry] = useState("El Salvador");
  const [prefix, setPrefix] = useState("");
  const [prefixTouched, setPrefixTouched] = useState(false);
  const [numTeachers, setNumTeachers] = useState(6);
  const [defaultPassword, setDefaultPassword] = useState("123456");
  const [adminPassword, setAdminPassword] = useState("");
  const [adminPwdTouched, setAdminPwdTouched] = useState(false);
  const [studentsPerGrade, setStudentsPerGrade] = useState({
    "7": 1, "8": 1, "9": 1, "10": 1, "11": 1,
  });
  const [teachersAllGrades, setTeachersAllGrades] = useState(false);

  // Result state
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const sheetRef = useRef(null);

  const effectivePrefix = useMemo(
    () => (prefixTouched && prefix ? slugifyPrefix(prefix) : slugifyPrefix(name)),
    [prefix, prefixTouched, name]
  );
  const effectiveAdminPwd = useMemo(
    () => (adminPwdTouched && adminPassword ? adminPassword : `${effectivePrefix}2026`),
    [adminPassword, adminPwdTouched, effectivePrefix]
  );

  const totalStudents = useMemo(
    () => GRADES.reduce((s, g) => s + (Number(studentsPerGrade[g]) || 0), 0),
    [studentsPerGrade]
  );

  const submit = async () => {
    if (!name.trim()) { toast.error("Indica el nombre del colegio"); return; }
    if (numTeachers < 1) { toast.error("Necesitas al menos 1 docente"); return; }
    setLoading(true);
    try {
      const body = {
        name: name.trim(),
        country: country.trim() || "El Salvador",
        prefix: effectivePrefix,
        admin_password: effectiveAdminPwd,
        default_password: defaultPassword || "123456",
        num_teachers: Number(numTeachers),
        students_per_grade: Object.fromEntries(
          GRADES.map(g => [g, Number(studentsPerGrade[g]) || 0])
        ),
        teachers_all_grades: teachersAllGrades,
      };
      const { data } = await axios.post(`${API}/admin/seed-school-pilot`, body, { withCredentials: true });
      setResult(data);
      toast.success(`Colegio piloto creado: ${data.school.name}`);
      // Smooth scroll to the printable sheet
      setTimeout(() => {
        sheetRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Error al crear el colegio piloto");
    } finally {
      setLoading(false);
    }
  };

  const resetAll = () => {
    setName(""); setCountry("El Salvador"); setPrefix(""); setPrefixTouched(false);
    setNumTeachers(6); setDefaultPassword("123456");
    setAdminPassword(""); setAdminPwdTouched(false);
    setStudentsPerGrade({ "7": 1, "8": 1, "9": 1, "10": 1, "11": 1 });
    setTeachersAllGrades(false);
    setResult(null);
  };

  const copyAll = () => {
    if (!result) return;
    const lines = [
      `Colegio: ${result.school.name} (${result.school.country})`,
      "",
      "ADMIN INSTITUCIONAL",
      `${result.admin.username}  |  ${result.admin.password}`,
      "",
      "DOCENTES",
      ...result.teachers.map(t =>
        `${t.username}  |  ${t.password}  |  ${t.grade ? `Grado ${t.grade}` : "Polivalente"}`
      ),
      "",
      "ESTUDIANTES",
      ...result.students.map(s =>
        `${s.username}  |  ${s.password}  |  Grado ${s.grade}  |  Docente: ${s.teacher_username}`
      ),
    ];
    navigator.clipboard.writeText(lines.join("\n")).then(
      () => toast.success("Credenciales copiadas al portapapeles"),
      () => toast.error("No se pudo copiar")
    );
  };

  const printSheet = () => window.print();

  return (
    <div className="space-y-6">
      {/* Wizard form */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm print:hidden">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shadow-md">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Wizard: Crear colegio piloto</h2>
            <p className="text-xs text-gray-500">
              Genera la institución + admin + docentes + alumnos en un solo paso.
              Las credenciales se muestran <span className="font-semibold">una sola vez</span> para imprimir y repartir.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Left: basic info */}
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold text-gray-700">Nombre del colegio *</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Colegio Champagnat"
                data-testid="pilot-name"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-700">País</Label>
              <Input
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                placeholder="El Salvador"
                data-testid="pilot-country"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-700">
                Prefijo de usuarios <span className="text-gray-400">(automático del nombre)</span>
              </Label>
              <Input
                value={prefixTouched ? prefix : effectivePrefix}
                onChange={(e) => { setPrefix(e.target.value); setPrefixTouched(true); }}
                placeholder="CHAMP"
                maxLength={8}
                data-testid="pilot-prefix"
              />
              <p className="text-[11px] text-gray-400 mt-1">
                Usuarios: {effectivePrefix}ADMIN, {effectivePrefix}DOC1..N, {effectivePrefix}7A, etc.
              </p>
            </div>
          </div>

          {/* Right: passwords + teachers */}
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold text-gray-700"># Docentes (1–30)</Label>
              <Input
                type="number" min={1} max={30}
                value={numTeachers}
                onChange={(e) => setNumTeachers(Math.max(1, Math.min(30, Number(e.target.value) || 0)))}
                data-testid="pilot-num-teachers"
              />
              <p className="text-[11px] text-gray-400 mt-1">
                Los primeros 5 se asignan a los grados 7–11; los extras quedan polivalentes.
              </p>
              <label className="flex items-start gap-2 mt-3 cursor-pointer" data-testid="pilot-teachers-all-grades-row">
                <input
                  type="checkbox"
                  checked={teachersAllGrades}
                  onChange={(e) => setTeachersAllGrades(e.target.checked)}
                  className="mt-0.5"
                  data-testid="pilot-teachers-all-grades"
                />
                <span className="text-[11px] text-gray-600 leading-tight">
                  <span className="font-semibold text-gray-800">Docentes polivalentes</span> — asigna a TODOS los docentes a TODOS los alumnos (sin grado fijo).
                </span>
              </label>
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-700">Contraseña del admin</Label>
              <Input
                value={adminPwdTouched ? adminPassword : effectiveAdminPwd}
                onChange={(e) => { setAdminPassword(e.target.value); setAdminPwdTouched(true); }}
                placeholder={`${effectivePrefix}2026`}
                data-testid="pilot-admin-pwd"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-700">Contraseña default (docentes y alumnos)</Label>
              <Input
                value={defaultPassword}
                onChange={(e) => setDefaultPassword(e.target.value)}
                placeholder="123456"
                data-testid="pilot-default-pwd"
              />
            </div>
          </div>
        </div>

        {/* Students per grade */}
        <div className="mt-6">
          <Label className="text-xs font-semibold text-gray-700">Estudiantes por grado</Label>
          <div className="grid grid-cols-5 gap-3 mt-2">
            {GRADES.map((g) => (
              <div key={g} className="bg-gray-50 rounded-xl p-3 text-center">
                <p className="text-[11px] font-semibold text-gray-500 mb-1">Grado {g}</p>
                <Input
                  type="number" min={0} max={50}
                  value={studentsPerGrade[g]}
                  onChange={(e) => setStudentsPerGrade((prev) => ({
                    ...prev, [g]: Math.max(0, Math.min(50, Number(e.target.value) || 0))
                  }))}
                  className="text-center"
                  data-testid={`pilot-students-grade-${g}`}
                />
              </div>
            ))}
          </div>
          <p className="text-[11px] text-gray-400 mt-2">
            Total: <span className="font-semibold text-gray-700">{totalStudents}</span> estudiantes. Usuarios: {effectivePrefix}7A, {effectivePrefix}7B, …, {effectivePrefix}11A, …
          </p>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Button
            onClick={submit}
            disabled={loading || !name.trim() || numTeachers < 1}
            className="bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white"
            data-testid="pilot-submit"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {loading ? "Creando..." : "Crear colegio piloto"}
          </Button>
          <Button variant="outline" onClick={resetAll} data-testid="pilot-reset">
            <RotateCcw className="w-4 h-4 mr-2" />Limpiar
          </Button>
        </div>
      </div>

      {/* Result: printable sheet */}
      {result && (
        <div
          ref={sheetRef}
          className="bg-white rounded-2xl border border-gray-200 shadow-sm pilot-sheet"
          data-testid="pilot-result"
        >
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-wrap gap-3 print:hidden">
            <div>
              <h3 className="font-bold text-gray-900">Credenciales generadas</h3>
              <p className="text-xs text-gray-500">
                Imprime o copia ahora — esta vista solo se muestra una vez.
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={copyAll} data-testid="pilot-copy-all">
                <Copy className="w-4 h-4 mr-2" />Copiar todo
              </Button>
              <Button onClick={printSheet} className="bg-gray-900 text-white" data-testid="pilot-print">
                <Printer className="w-4 h-4 mr-2" />Imprimir / PDF
              </Button>
            </div>
          </div>

          {/* The actual printable content */}
          <div className="p-6 space-y-6">
            <div className="border-b border-gray-200 pb-4">
              <h1 className="text-2xl font-bold text-gray-900">{result.school.name}</h1>
              <p className="text-sm text-gray-500">
                {result.school.country} · Prefijo: <span className="font-mono">{result.school.prefix}</span>
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {result.summary.teachers_count} docentes · {result.summary.students_count} estudiantes
              </p>
            </div>

            {/* Admin */}
            <section data-testid="pilot-sheet-admin">
              <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-violet-500" /> Administrador institucional
              </h2>
              <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-50 text-gray-600 text-xs">
                  <tr>
                    <th className="text-left px-3 py-2">Usuario</th>
                    <th className="text-left px-3 py-2">Contraseña</th>
                    <th className="text-left px-3 py-2">Rol</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-gray-100">
                    <td className="px-3 py-2 font-mono font-semibold">{result.admin.username}</td>
                    <td className="px-3 py-2 font-mono">{result.admin.password}</td>
                    <td className="px-3 py-2">Admin {result.school.name}</td>
                  </tr>
                </tbody>
              </table>
            </section>

            {/* Teachers */}
            <section data-testid="pilot-sheet-teachers">
              <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-blue-500" /> Docentes ({result.teachers.length})
              </h2>
              <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-50 text-gray-600 text-xs">
                  <tr>
                    <th className="text-left px-3 py-2">Usuario</th>
                    <th className="text-left px-3 py-2">Contraseña</th>
                    <th className="text-left px-3 py-2">Grado</th>
                    <th className="text-left px-3 py-2">Nombre</th>
                  </tr>
                </thead>
                <tbody>
                  {result.teachers.map((t) => (
                    <tr key={t.username} className="border-t border-gray-100">
                      <td className="px-3 py-2 font-mono font-semibold">{t.username}</td>
                      <td className="px-3 py-2 font-mono">{t.password}</td>
                      <td className="px-3 py-2">{t.grade ? `Grado ${t.grade}` : "Polivalente"}</td>
                      <td className="px-3 py-2 text-gray-600">{t.name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            {/* Students */}
            <section data-testid="pilot-sheet-students">
              <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide flex items-center gap-2 mb-2">
                <GraduationCap className="w-4 h-4 text-emerald-500" /> Estudiantes ({result.students.length})
              </h2>
              <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-50 text-gray-600 text-xs">
                  <tr>
                    <th className="text-left px-3 py-2">Usuario</th>
                    <th className="text-left px-3 py-2">Contraseña</th>
                    <th className="text-left px-3 py-2">Grado</th>
                    <th className="text-left px-3 py-2">Docente asignado</th>
                  </tr>
                </thead>
                <tbody>
                  {result.students.map((s) => (
                    <tr key={s.username} className="border-t border-gray-100">
                      <td className="px-3 py-2 font-mono font-semibold">{s.username}</td>
                      <td className="px-3 py-2 font-mono">{s.password}</td>
                      <td className="px-3 py-2">{s.grade}</td>
                      <td className="px-3 py-2 font-mono text-xs text-gray-500">{s.teacher_username}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            <p className="text-[11px] text-gray-400 mt-2 border-t border-gray-100 pt-3">
              QHUB.BUDDY · Generado el {new Date().toLocaleString("es-ES")}. Conserva esta hoja en lugar seguro.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
